import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AlertComponent } from '../../shared/components/alert/alert.component';
import { FileUploadService } from './service/file-upload.service';
import { AlertService } from '../../shared/services/alert.service';
import { BrowserAccessService } from '../../shared/services/browser-access.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FileButtonComponent } from '../file-button/file-button.component';
import { TranslationOptionsComponent } from '../translation-options/translation-options.component';
import { TranslationOptionsService } from '../translation-options/service/translation-options.service';
import { LanguagesService } from '../../shared/services/languages.service';

@Component({
  selector: 'app-file-upload',
  imports: [
    FormsModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    ClipboardModule,
    CommonModule,
    FormsModule,
    AlertComponent,
    TranslateModule,
    FileButtonComponent,
    TranslationOptionsComponent,
  ],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss',
})
export class FileUploadComponent implements OnInit {
  private readonly fileUploadService = inject(FileUploadService);
  private readonly browserCachingService = inject(BrowserAccessService);
  private readonly alertService = inject(AlertService);
  private readonly translate = inject(TranslateService);
  private readonly translationOptions = inject(TranslationOptionsService);
  private readonly languagesService = inject(LanguagesService);

  public selectedSourceLanguage = '';
  public selectedTargetLanguage = '';
  public sourceLanguages = this.languagesService.sourceLangs$;
  public targetLanguages = this.languagesService.targetLangs$;
  public isFileDropped = false;
  public isLoading = false;
  selectedFile: File | null = null;
  isDragOver = false;
  allFiles: File[] = [];
  allowedFileTypes = [
    'text/plain',
    'text/csv',
    'text/html',
    'application/x-subrip',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/pdf',
    'application/xliff+xml',
    'application/msword',
    'application/vnd.ms-exel',
    'application/vnd.ms-powerpoint',
  ];

  ngOnInit() {
    this.CheckForDefaultSourceLanguage();
    this.CheckForDefaultTargetLanguage();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const filesAmount = input.files ? input.files.length : 0;

    if (input.files && filesAmount === 1) {
      this.selectedFile = input.files[0];

      if (!this.allowedFileTypes.includes(this.selectedFile?.type as string)) {
        this.alertService.showAlert(
          'error',
          this.translate.instant('Alert.ValidFileTypeAlertMessage'),
        );
        return;
      }

      this.isFileDropped = true;
    } else if (filesAmount > 1) {
      this.alertService.showAlert('error', this.translate.instant('Alert.OnlyOneFileAlertMessage'));
    } else {
      this.alertService.showAlert('error', this.translate.instant('Alert.SelectFileAlertMessage'));
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onSelectionChangeTargetLanguage(event: any) {
    this.selectedTargetLanguage = event.value;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onSelectionChangeSourceLanguage(event: any) {
    this.selectedSourceLanguage = event.value;
  }

  handleFileDelete(isFileDeleted: boolean): void {
    this.selectedFile = null;
    this.isFileDropped = isFileDeleted;
  }

  translateFile() {
    const formData = new FormData();
    let filename: string;

    if (this.selectedSourceLanguage === '' || this.selectedTargetLanguage === '') {
      this.alertService.showAlert(
        'error',
        this.translate.instant('Alert.SelectLanguageAlertMessage'),
      );
      return;
    }

    if (this.selectedFile) {
      this.isLoading = true;

      formData.append('file', this.selectedFile);
      formData.append('sourceLanguage', this.selectedSourceLanguage);
      formData.append('targetLanguage', this.selectedTargetLanguage);
      formData.append('formality', this.translationOptions.selectedFormality);

      this.browserCachingService.setItem(
        'defaultDocumentLanguage',
        this.selectedSourceLanguage.toString(),
      );

      this.browserCachingService.setItem(
        'defaultDocumentTargetLanguage',
        this.selectedTargetLanguage.toString(),
      );

      this.fileUploadService.translateFile(formData).subscribe({
        next: (response) => {
          const blob = new Blob([response.body], { type: 'application/octet-stream' });

          if (this.selectedFile) {
            filename = this.PrepareFileName(this.selectedFile?.name, this.selectedTargetLanguage);
          }

          if (filename === undefined) {
            filename = 'translatedFile.txt';
          }

          this.isLoading = false;
          this.downloadFile(blob, filename);
        },
        error: (error) => {
          /// If a file is located in a onedrive or sharepoint and it is opened in word a "Unkown Error" is thrown.
          /// This is a workaround to show a more specific error message.
          if (error.statusText && error.statusText.includes('Unknown')) {
            this.alertService.showAlert(
              'error',
              this.translate.instant('Alert.OpenFileAlertMessage'),
            );
          } else {
            this.alertService.showAlert(
              'error',
              this.translate.instant('Alert.TranslateErrorMessage'),
            );
          }
          this.isLoading = false;
          console.log(error);
        },
      });
    } else {
      this.alertService.showAlert('error', this.translate.instant('Alert.SelectFileAlertMessage'));
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave() {
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    const file = event.dataTransfer?.files[0];
    const fileAmount = event.dataTransfer?.files.length;

    if (file && fileAmount === 1) {
      if (!this.allowedFileTypes.includes(file?.type as string)) {
        this.alertService.showAlert(
          'error',
          this.translate.instant('Alert.ValidFileTypeAlertMessage'),
        );
        return;
      }
      this.selectedFile = file;
      this.allFiles.push(this.selectedFile);
      this.isFileDropped = true;
    } else if (file && (fileAmount ?? 0) > 1) {
      this.alertService.showAlert('error', this.translate.instant('Alert.OnlyOneFileAlertMessage'));
    } else {
      this.alertService.showAlert('error', this.translate.instant('Alert.SelectFileAlertMessage'));
    }
  }

  browseForFile(event: Event) {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length === 0) {
      this.selectedFile = input.files[0];
    } else if (input.files && input.files.length > 0) {
      this.alertService.showAlert('error', this.translate.instant('Alert.OnlyOneFileAlertMessage'));
    } else {
      this.alertService.showAlert('error', this.translate.instant('Alert.SelectFileAlertMessage'));
    }
  }

  downloadFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  private CheckForDefaultSourceLanguage() {
    const defaultLanguage = this.browserCachingService.getItem('defaultDocumentLanguage');
    console.log(`default language is ${defaultLanguage}`);

    if (defaultLanguage === 'undefined') {
      this.browserCachingService.removeItem('defaultDocumentLanguage');
    }
    this.selectedSourceLanguage = defaultLanguage;
  }

  private CheckForDefaultTargetLanguage() {
    const defaultTargetLanguage = this.browserCachingService.getItem(
      'defaultDocumentTargetLanguage',
    );
    console.log(`default language is ${defaultTargetLanguage}`);

    if (defaultTargetLanguage === 'undefined') {
      this.browserCachingService.removeItem('defaultDocumentTargetLanguage');
    }
    this.selectedTargetLanguage = defaultTargetLanguage;
  }

  private PrepareFileName(fileName: string, targetLanguage: string): string {
    let dotIndex = fileName.lastIndexOf('.');

    let ext = '';

    if (dotIndex !== -1) {
      ext = fileName.substring(dotIndex + 1);
    } else {
      dotIndex = fileName.length;
    }

    const newFileName =
      fileName.substring(0, dotIndex) +
      '_' +
      this.getCurrentDateYYYYMMDD() +
      '_' +
      targetLanguage.toUpperCase() +
      '.' +
      ext;
    return newFileName;
  }

  private getCurrentDateYYYYMMDD(): string {
    const now = new Date();

    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0'); // Monate: 0–11
    const day = now.getDate().toString().padStart(2, '0');

    return `${year}${month}${day}`;
  }
}
